/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { Typography2, breakpoints } from '@coursera/cds-core';

import TopReviewCarouselCard, { type TopReviewCardReview } from 'bundles/reviews/components/TopReviewCarouselCard';

import _t from 'i18n!nls/reviews';

const CARD_WIDTH = 450;
const CARD_WIDTH_MOBILE = 280;
const CARD_GAP = 16;

const styles = {
  section: css`
    max-width: 100%;
  `,
  sectionTitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  carouselContainer: css`
    [data-testid='overflow-carousel-content'] {
      display: flex;
      align-items: stretch;
      gap: ${CARD_GAP}px;
    }

    /* OverflowCarousel wraps each child in a [role="listitem"]; make it pass height down so cards match */
    [data-testid='overflow-carousel-content'] [role='listitem'] {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `,
  cardItem: css`
    width: ${CARD_WIDTH}px;
    flex-shrink: 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    ${breakpoints.down('sm')} {
      width: ${CARD_WIDTH_MOBILE}px;
    }
  `,
};

type TopReviewsSectionReview = TopReviewCardReview & { key: string };

type TopReviewsSectionProps = {
  title: string;
  reviews: Array<TopReviewsSectionReview>;
};

const TopReviewsSection = ({ title, reviews }: TopReviewsSectionProps) => {
  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <section css={styles.section} aria-label={_t('Top reviews carousel')}>
      <Typography2 variant="subtitleLarge" component="h2" css={styles.sectionTitle}>
        {title}
      </Typography2>
      <div css={styles.carouselContainer}>
        <OverflowCarousel
          showNavigationButtons
          shouldAllowSpaceForHoverItems={false}
          shouldStartOverflowAtMediumBreakpoint
        >
          {reviews.map(({ key, ...review }) => (
            <div key={key} css={styles.cardItem}>
              <TopReviewCarouselCard review={review} />
            </div>
          ))}
        </OverflowCarousel>
      </div>
    </section>
  );
};

export default TopReviewsSection;
